import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  useQueueStatusStore,
  useQueueItemsStore,
  useSubmitQueueAnswers,
  useSkipQueueItem,
  useRetryQueueItem,
  useDeleteQueueItem,
} from '../../stores/queue';
import { useQueueDetailStore } from '../../stores/queue-detail';
import { usePresenceStore, useRegisterPresence } from '../../stores/presence';
import { createQaFormStore } from '../../lib/live-form-store';
import { useConnectionStore } from '../../stores/connection';
import { PresenceAvatars } from '../../components/ui/presence-avatars';
import { LiveField } from '../../components/assets/LiveField';
import { toast } from 'sonner';
import {
  Loader2,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  SkipForward,
  RefreshCw,
  Trash2,
  Info,
  ImageIcon,
  ArrowLeft,
  PanelRightClose,
  PanelRightOpen,
  Circle,
  ChevronRight,
} from 'lucide-react';
import type { AssetQA, QueueItem } from '../../types/api';

const CONFIRM_TIMEOUT = 3000;
const DOUBLE_CLICK_GUARD = 400;

function ConfirmDeleteButton({
  onConfirm,
  disabled,
  label = 'Delete',
  variant = 'destructive' as const,
}: {
  onConfirm: () => void;
  disabled?: boolean;
  label?: string;
  variant?: 'destructive' | 'outline' | 'ghost';
}) {
  const [phase, setPhase] = useState<'idle' | 'armed' | 'confirming'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const reset = useCallback(() => {
    setPhase('idle');
    clearTimeout(timerRef.current);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleClick = () => {
    if (disabled) return;

    if (phase === 'idle') {
      setPhase('armed');
      setTimeout(() => setPhase('confirming'), DOUBLE_CLICK_GUARD);
      timerRef.current = setTimeout(reset, CONFIRM_TIMEOUT);
      return;
    }

    if (phase === 'armed') return;

    if (phase === 'confirming') {
      reset();
      onConfirm();
    }
  };

  const active = phase !== 'idle';
  const isGhost = variant === 'ghost';

  return (
    <Button
      variant={active ? 'destructive' : variant}
      size={isGhost ? 'icon' : 'sm'}
      onClick={handleClick}
      disabled={disabled}
      title={active ? 'Click to confirm' : label}
      className={`relative overflow-hidden ${active && isGhost ? 'rounded-full' : ''}`}
    >
      <Trash2 className={`relative z-10 ${isGhost ? 'h-4 w-4' : 'h-4 w-4 mr-1'} ${!active && isGhost ? 'text-muted-foreground' : ''}`} />
      {!isGhost && (
        <span className="relative z-10">{active ? 'Sure?' : label}</span>
      )}
      {active && (
        <span
          className="absolute inset-0 bg-red-900/30 origin-right"
          style={{
            animation: `shrink-right ${CONFIRM_TIMEOUT}ms linear forwards`,
          }}
        />
      )}
    </Button>
  );
}

const useQaForm = createQaFormStore();

export function AssetReviewPage() {
  useQueueStatusStore.live();
  useQueueItemsStore.live();
  const allQueueItems = useQueueItemsStore(s => s.items);
  const itemsLoading = useQueueItemsStore(s => s._loading);
  const processingList = useMemo(() => allQueueItems.filter(i => i.status === 'processing'), [allQueueItems]);
  const activeItems = useMemo(() => allQueueItems.filter(i => i.status === 'awaiting_review'), [allQueueItems]);
  const failedList = useMemo(() => allQueueItems.filter(i => i.status === 'failed'), [allQueueItems]);

  const [selectedId, setSelectedId] = useState<number | undefined>();

  const setQueueId = useQueueDetailStore(s => s.setQueueId);
  useEffect(() => { setQueueId(selectedId); }, [selectedId, setQueueId]);
  useQueueDetailStore.live();
  const detail = useQueueDetailStore(s => s.item);
  const detailLoading = useQueueDetailStore(s => s._loading);

  const submitAnswers = useSubmitQueueAnswers();
  const skipItem = useSkipQueueItem();
  const retryItem = useRetryQueueItem();
  const deleteItem = useDeleteQueueItem();
  const instanceId = useConnectionStore(s => s.instanceId);
  usePresenceStore.live({ entity: "queue" });
  const presenceMap = usePresenceStore(s => s.presence);
  useRegisterPresence("queue", selectedId, !!selectedId);

  const [skippedQuestions, setSkippedQuestions] = useState<Set<number>>(new Set());
  const [skipReasons, setSkipReasons] = useState<Record<number, string>>({});
  const [queueOpen, setQueueOpen] = useState(true);
  const [focusedQaId, setFocusedQaId] = useState<number | null>(null);

  const initialAnswers = useMemo(() => {
    if (!detail?.qas) return {};
    const map: Record<string, string> = {};
    for (const qa of detail.qas) {
      if (qa.answer && !qa.answered_at) map[String(qa.qa_id)] = qa.answer;
    }
    return map;
  }, [detail?.queue_id]);

  const qaEdits = useQaForm(
    detail?.status === 'awaiting_review' ? selectedId : undefined,
    initialAnswers
  );

  const allItems = [...processingList, ...activeItems, ...failedList];

  useEffect(() => {
    if (itemsLoading) return;
    if (selectedId && !allItems.some((item) => item.queue_id === selectedId)) {
      qaEdits.discard();
      setSelectedId(allItems[0]?.queue_id);
      setSkippedQuestions(new Set());
      setSkipReasons({});
      setFocusedQaId(null);
    } else if (!selectedId && allItems.length > 0) {
      setSelectedId(allItems[0]?.queue_id);
    }
  }, [allItems.map((i) => i.queue_id).join(','), itemsLoading]);

  const unansweredQAs = detail?.qas?.filter((qa: AssetQA) => !qa.answer && !qa.skipped_at) || [];
  const allQAs = [...(detail?.qas || [])].sort((a, b) => a.qa_id - b.qa_id);

  useEffect(() => {
    if (detail && focusedQaId === null) {
      const firstUnanswered = unansweredQAs[0];
      if (firstUnanswered) {
        setFocusedQaId(firstUnanswered.qa_id);
      }
    }
  }, [detail?.queue_id]);



  const handleToggleSkipMode = (qaId: number) => {
    setSkippedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(qaId)) {
        newSet.delete(qaId);
      } else {
        newSet.add(qaId);
      }
      return newSet;
    });
  };

  const handleSkipReasonChange = (qaId: number, reason: string) => {
    setSkipReasons((prev) => ({ ...prev, [qaId]: reason }));
  };

  const handleSubmit = async () => {
    if (!selectedId) return;

    try {
      if (qaEdits.isDirty) await qaEdits.submit();
    } catch {
      toast.error('Failed to save edits');
      return;
    }

    const merged = qaEdits.answers;
    const answerPayload = unansweredQAs
      .filter((qa: AssetQA) => !skippedQuestions.has(qa.qa_id) && merged[String(qa.qa_id)]?.trim())
      .map((qa: AssetQA) => ({
        qa_id: qa.qa_id,
        answer: merged[String(qa.qa_id)],
      }));

    const skipPayload = unansweredQAs
      .filter((qa: AssetQA) => skippedQuestions.has(qa.qa_id))
      .map((qa: AssetQA) => ({
        qa_id: qa.qa_id,
        skip_reason: skipReasons[qa.qa_id] || '',
      }));

    if (answerPayload.length === 0 && skipPayload.length === 0) {
      toast.error('Please answer or skip at least one question');
      return;
    }

    try {
      await submitAnswers.mutateAsync({ queueId: selectedId, answers: answerPayload, skipped: skipPayload });
      toast.success('Responses submitted, processing...');
      setSkippedQuestions(new Set());
      setSkipReasons({});
      setFocusedQaId(null);
    } catch (error) {
      toast.error(`Failed to submit: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const advanceToNextUnanswered = () => {
    const currentIndex = allQAs.findIndex((qa) => qa.qa_id === focusedQaId);
    if (currentIndex === -1) return;

    for (let i = currentIndex + 1; i < allQAs.length; i++) {
      const qa = allQAs[i];
      if (qa && !qa.answer && !qa.skipped_at) {
        setFocusedQaId(qa.qa_id);
        return;
      }
    }
  };

  const handleSkip = async () => {
    if (!selectedId) return;

    try {
      await skipItem.mutateAsync({ queueId: selectedId });
      toast.success('Asset promoted to Inventoried');
      qaEdits.discard();
      setSelectedId(undefined);
    } catch (error) {
      toast.error(`Failed to skip: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRetry = async (queueId: number) => {
    try {
      await retryItem.mutateAsync({ queueId });
      toast.success('Retrying...');
    } catch (error) {
      toast.error(`Retry failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDelete = async (queueId: number) => {
    try {
      await deleteItem.mutateAsync({ queueId });
      toast.success('Item deleted');
      qaEdits.discard();
      setSelectedId(undefined);
    } catch (error) {
      toast.error(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const renderQueueItem = (item: QueueItem) => (
    <button
      key={item.queue_id}
      onClick={() => {
        qaEdits.discard();
        setSelectedId(item.queue_id);
        setSkippedQuestions(new Set());
        setSkipReasons({});
        setFocusedQaId(null);
      }}
      className={`w-full text-left rounded-lg p-3 transition-colors ${
        selectedId === item.queue_id
          ? 'bg-accent'
          : 'hover:bg-muted'
      }`}
    >
      <div className="flex gap-3">
        {item.primary_photo_url ? (
          <img
            src={item.primary_photo_url}
            alt=""
            className="h-12 w-auto rounded shrink-0"
          />
        ) : (
          <div className="h-12 w-12 rounded bg-muted flex items-center justify-center shrink-0">
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm truncate flex-1">
              {item.asset_nickname || 'Draft Asset'}
            </p>
            {presenceMap?.[item.queue_id] && (
              <PresenceAvatars
                viewers={presenceMap[item.queue_id] ?? []}
                currentInstanceId={instanceId}
                max={2}
              />
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            {item.status === 'failed' ? (
              <Badge variant="destructive" className="text-xs">
                Failed
              </Badge>
            ) : item.status === 'processing' ? (
              <Badge variant="secondary" className="text-xs">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Processing
              </Badge>
            ) : (
              <>
                {item.unanswered_count != null && item.unanswered_count > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    {item.unanswered_count}
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">
                  Round {item.current_round}
                </Badge>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );

  return (
    <>
      <PageHeader
        title="Review Queue"
        description="Review AI-analyzed assets and answer questions"
        breadcrumbs={[
          { label: 'Assets', href: '/assets' },
          { label: 'Review' },
        ]}
      />

      <main className="flex-1 overflow-hidden bg-muted/40">
        <div className="flex h-full">
          <div className="flex-1 overflow-y-auto">
            {!selectedId ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p className="font-medium">Select an item to review</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose an asset from the queue on the right
                </p>
              </div>
            ) : detailLoading ? (
              <div className="p-6 space-y-4">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : detail ? (
              <div className="p-6 space-y-6">
                {detail.status === 'failed' && (
                  <Card className="border-destructive">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-destructive">
                          <AlertCircle className="h-4 w-4" />
                          <span className="text-sm">
                            {detail.error_message || 'Processing failed'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRetry(detail.queue_id)}
                            disabled={retryItem.isPending}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Retry
                          </Button>
                          <ConfirmDeleteButton
                            onConfirm={() => handleDelete(detail.queue_id)}
                            disabled={deleteItem.isPending}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {detail.photos && detail.photos.length > 0 && (
                  <div className="flex flex-wrap gap-3">
                    {detail.photos.map((photo) => (
                      <div key={photo.photo_id} className="relative">
                        <img
                          src={photo.photo_url}
                          alt={photo.ai_suggested_title || photo.filename || ''}
                          className="h-48 w-auto rounded-lg"
                        />
                        {photo.ai_suggested_title && (
                          <p className="text-xs text-muted-foreground mt-1 truncate max-w-48">
                            {photo.ai_suggested_title}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {detail.extraction_result?.hierarchical_identification && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        AI Identified: {detail.extraction_result.asset_nickname}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(
                          detail.extraction_result.hierarchical_identification
                        ).map(([key, value]) =>
                          value ? (
                            <Badge key={key} variant="secondary">
                              {value as string}
                            </Badge>
                          ) : null
                        )}
                      </div>
                      {detail.extraction_result.confidenceMessage && (
                        <p className="text-xs text-muted-foreground mt-3">
                          {detail.extraction_result.confidenceMessage}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {allQAs.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Review Questions</CardTitle>
                      <CardDescription>
                        Answer what you can - we'll use this to refine the asset information
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {allQAs.map((qa: AssetQA) => {
                        const f = qaEdits.field(qa.qa_id);
                        const hasDbAnswer = !!qa.answered_at && !!qa.answer;
                        const hasDbSkip = !!qa.skipped_at;
                        const hasSessionAnswer = !!f.value?.trim();
                        const hasSessionSkip = skippedQuestions.has(qa.qa_id);
                        const isCompleted = hasDbAnswer || hasDbSkip || hasSessionAnswer || hasSessionSkip;
                        const isFocused = qa.qa_id === focusedQaId;
                        const isEditable = !hasDbAnswer && !hasDbSkip;
                        const isSkipMode = skippedQuestions.has(qa.qa_id);

                        const answerPreview = hasSessionAnswer
                          ? f.value
                          : hasDbAnswer
                          ? qa.answer
                          : hasSessionSkip || hasDbSkip
                          ? 'Skipped'
                          : '';

                        if (!isFocused) {
                          return (
                            <button
                              key={qa.qa_id}
                              onClick={() => setFocusedQaId(qa.qa_id)}
                              className="w-full text-left px-3 py-2 hover:bg-muted/50 rounded-md cursor-pointer transition-colors"
                            >
                              <div className="flex items-start gap-2">
                                {isCompleted ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                                ) : (
                                  <Circle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm truncate block">{qa.question}</span>
                                </div>
                                {f.lockedBy && (
                                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full leading-none shrink-0">
                                    {f.lockedBy.userName}
                                  </span>
                                )}
                                {answerPreview && (
                                  <>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                                      {answerPreview}
                                    </span>
                                  </>
                                )}
                              </div>
                            </button>
                          );
                        }

                        if (isEditable) {
                          return (
                            <div key={qa.qa_id} className="px-3 py-3 bg-muted/30 rounded-md border-l-2 border-primary space-y-3">
                              <Label>{qa.question}</Label>

                              {!isSkipMode ? (
                                <>
                                  {Array.isArray(qa.quick_responses) && qa.quick_responses.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {qa.quick_responses.map((resp) => (
                                        <Button
                                          key={resp}
                                          type="button"
                                          variant={
                                            f.value === resp
                                              ? 'default'
                                              : 'outline'
                                          }
                                          size="sm"
                                          onClick={() => f.onChange(resp)}
                                        >
                                          {resp}
                                        </Button>
                                      ))}
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleToggleSkipMode(qa.qa_id)}
                                      >
                                        Skip
                                      </Button>
                                    </div>
                                  )}

                                  <LiveField lockedBy={f.lockedBy}>
                                    <Textarea
                                      placeholder="Type your answer..."
                                      value={f.value || ''}
                                      onChange={(e) => f.onChange(e.target.value)}
                                      onFocus={f.onFocus}
                                      onBlur={f.onBlur}
                                      onKeyDown={(e) => {
                                        if (e.ctrlKey && e.key === 'Enter' && f.value?.trim()) {
                                          advanceToNextUnanswered();
                                        }
                                      }}
                                      disabled={!f.isEditable}
                                      rows={2}
                                    />
                                  </LiveField>

                                  {(!qa.quick_responses || qa.quick_responses.length === 0) && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleToggleSkipMode(qa.qa_id)}
                                    >
                                      Skip
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleToggleSkipMode(qa.qa_id)}
                                    >
                                      <ArrowLeft className="h-4 w-4 mr-1" />
                                      Back
                                    </Button>
                                    <Badge variant="secondary">Skip question</Badge>
                                  </div>

                                  <Textarea
                                    placeholder="Optional: Why are you skipping this? (helps improve future questions)"
                                    value={skipReasons[qa.qa_id] || ''}
                                    onChange={(e) =>
                                      handleSkipReasonChange(qa.qa_id, e.target.value)
                                    }
                                    rows={2}
                                  />
                                </>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div key={qa.qa_id} className="px-3 py-3 bg-muted/30 rounded-md border-l-2 border-primary space-y-2">
                            <Label>{qa.question}</Label>
                            {qa.answer && (
                              <p className="text-sm font-medium">{qa.answer}</p>
                            )}
                            {qa.skipped_at && (
                              <p className="text-sm italic text-muted-foreground">
                                Skipped{qa.skip_reason && `: ${qa.skip_reason}`}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}

                {detail.status === 'awaiting_review' && (
                  <div className="flex gap-3 sticky bottom-0 bg-muted/40 py-4">
                    <ConfirmDeleteButton
                      variant="ghost"
                      onConfirm={() => handleDelete(detail.queue_id)}
                      disabled={deleteItem.isPending}
                    />
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleSkip}
                      disabled={skipItem.isPending}
                    >
                      <SkipForward className="h-4 w-4 mr-2" />
                      Skip
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleSubmit}
                      disabled={
                        submitAnswers.isPending ||
                        !unansweredQAs.every(
                          (qa: AssetQA) => skippedQuestions.has(qa.qa_id) || qaEdits.answers[String(qa.qa_id)]?.trim()
                        )
                      }
                    >
                      {submitAnswers.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <MessageSquare className="h-4 w-4 mr-2" />
                      )}
                      Submit Answers
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="shrink-0 border-l bg-background flex flex-col">
            <button
              onClick={() => setQueueOpen(!queueOpen)}
              className="p-2 border-b flex justify-center hover:bg-muted transition-colors"
              title={queueOpen ? 'Collapse queue' : 'Expand queue'}
            >
              {queueOpen ? (
                <PanelRightClose className="h-4 w-4" />
              ) : (
                <PanelRightOpen className="h-4 w-4" />
              )}
            </button>
            {queueOpen && (
              <div className="overflow-y-auto flex-1 w-80">
                {itemsLoading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : allItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center h-full">
                    <CheckCircle2 className="h-12 w-12 text-muted-foreground/50 mb-3" />
                    <p className="font-medium">All caught up!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      No assets awaiting review
                    </p>
                  </div>
                ) : (
                  <>
                    {processingList.length > 0 && (
                      <div>
                        <div className="p-4 border-b">
                          <h2 className="font-semibold text-sm flex items-center gap-2">
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                            Processing ({processingList.length})
                          </h2>
                        </div>
                        <div className="p-2 space-y-1">
                          {processingList.map(renderQueueItem)}
                        </div>
                      </div>
                    )}

                    {activeItems.length > 0 && (
                      <div>
                        <div className="p-4 border-b">
                          <h2 className="font-semibold text-sm">
                            Awaiting Review ({activeItems.length})
                          </h2>
                        </div>
                        <div className="p-2 space-y-1">
                          {activeItems.map(renderQueueItem)}
                        </div>
                      </div>
                    )}

                    {failedList.length > 0 && (
                      <div>
                        <div className="p-4 border-b">
                          <h2 className="font-semibold text-sm text-destructive">
                            Failed ({failedList.length})
                          </h2>
                        </div>
                        <div className="p-2 space-y-1">
                          {failedList.map(renderQueueItem)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
